// AI-CONTEXT: See docs/features/msi-onderbouwing-transparency.md
import useStandaardOnderbouwing from './useStandaardOnderbouwing'
import { isVersionedMsiEffect } from '../helpers/msiMonetisationHelpers/adaptStandaardOnderbouwing'
import { findOnderbouwingEntry } from '../helpers/msiMonetisationHelpers/buildOnderbouwingIndex'

// Which onderbouwing belongs to an MSI effect, decided in one place for both
// screens that open MsiOnderbouwingModal (ParticipationValueDetail, ScoreBlock).
//
//   effect with a version stamp → its own published document
//                                 (useStandaardOnderbouwing)
//   legacy /database effect     → the demo /monetarisering/onderbouwing
//                                 document (useMsiOnderbouwing's index)
//
// Never a mix: the demo's name fallback would hand a 1.1 effect another
// version's amounts — the bug this fixes (Vitaminds, 2026-11-03).

/**
 * @typedef {object} OnderbouwingEffectRef
 * @property {string} msiId - OutputEffects.MSIId
 * @property {string} name - Effect name, for the demo's fallback join and the modal header
 * @property {string|null} [standaardVersion] - Version the effect is valued at, null for legacy
 */

/**
 * Whether the effect has an onderbouwing to open.
 *
 * @param {object|null} legacyIndex - useMsiOnderbouwing's index
 * @param {OnderbouwingEffectRef} effect
 * @returns {boolean}
 */
export const onderbouwingAvailable = (legacyIndex, effect) =>
    isVersionedMsiEffect(effect.msiId, effect.standaardVersion)
    || !!findOnderbouwingEntry(legacyIndex, effect.msiId, effect.name)

const CLOSED = { entry: null, meta: null, loading: false, error: null, effectName: '' }

/**
 * Props for MsiOnderbouwingModal for the effect whose modal is open.
 *
 * @param {OnderbouwingEffectRef|null} effect - The effect whose modal is open, null when closed
 * @param {object|null} legacyIndex - useMsiOnderbouwing's index
 * @param {object|null} legacyMeta - useMsiOnderbouwing's meta
 * @returns {{entry: object|null, meta: object|null, loading: boolean, error: Error|null, effectName: string}}
 */
const useOnderbouwingForEffect = (effect, legacyIndex, legacyMeta) => {
    const versioned = !!effect && isVersionedMsiEffect(effect.msiId, effect.standaardVersion)
    const fromDocument = useStandaardOnderbouwing(versioned ? effect.msiId : '', versioned ? effect.standaardVersion : '')

    if (!effect) return CLOSED
    if (versioned) return { ...fromDocument, effectName: effect.name }
    return {
        entry: findOnderbouwingEntry(legacyIndex, effect.msiId, effect.name),
        meta: legacyMeta,
        loading: false,
        error: null,
        effectName: effect.name,
    }
}

export default useOnderbouwingForEffect
